// src/components/AiGenerateButton.tsx
//
// Small reusable "✨ Generate with AI" button. It owns its own loading spinner
// and inline error text so callers stay thin — the parent just supplies `run`,
// which performs the generation and resolves to an error message to display
// (or `null` on success).
import * as React from 'react';
import { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';

/**
 * Coerce an unknown thrown value into a user-readable STRING.
 *
 * This is the crash guard for this component: `error` is rendered directly as a
 * React child, so it must never be an object/array. A FastAPI 422 returns
 * `detail` as an array of `{loc, msg, type}` objects; rendering that throws
 * "Objects are not valid as a React child" and (with no app-level error
 * boundary) blanks the whole page. Try each likely message source in priority
 * order and return the first that resolves to a non-empty string.
 */
function toMessageString(v: unknown): string | null {
  if (typeof v === 'string') return v.trim() || null;
  if (Array.isArray(v)) {
    // FastAPI validation errors: [{ loc, msg, type }, ...]
    const msgs = v
      .map((item) =>
        item && typeof item === 'object' && typeof (item as { msg?: unknown }).msg === 'string'
          ? (item as { msg: string }).msg
          : typeof item === 'string'
          ? item
          : '',
      )
      .filter(Boolean);
    return msgs.length ? msgs.join(', ') : null;
  }
  if (v && typeof v === 'object') {
    const obj = v as { msg?: unknown; detail?: unknown; message?: unknown };
    if (typeof obj.msg === 'string') return obj.msg.trim() || null;
    if (typeof obj.message === 'string') return obj.message.trim() || null;
    if (typeof obj.detail === 'string') return obj.detail.trim() || null;
    return null;
  }
  return null;
}

function errorToMessage(e: unknown): string {
  const ax = e as {
    response?: { data?: { detail?: unknown; message?: unknown } };
    message?: unknown;
  };
  const candidates: unknown[] = [
    ax?.response?.data?.message,
    ax?.response?.data?.detail,
    ax?.message,
  ];
  for (const c of candidates) {
    const s = toMessageString(c);
    if (s) return s;
  }
  return 'Something went wrong. Please try again.';
}

interface AiGenerateButtonProps {
  /** Performs the generation. Resolve to an error message to show, or null on success. */
  run: () => Promise<string | null>;
  disabled?: boolean;
  /** Tooltip shown while disabled, e.g. "Enter a title first". */
  disabledHint?: string;
  idleLabel: string;
  busyLabel?: string;
  className?: string;
}

export default function AiGenerateButton({
  run,
  disabled = false,
  disabledHint,
  idleLabel,
  busyLabel = 'Generating…',
  className = '',
}: AiGenerateButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    if (loading || disabled) return;
    setLoading(true);
    setError(null);
    try {
      const err = await run();
      if (err) setError(err);
    } catch (e: unknown) {
      // Always store a string — never an object/array, which would crash render.
      setError(errorToMessage(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`flex flex-col items-end ${className}`}>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || loading}
        title={disabled ? disabledHint : undefined}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg
          bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-sm
          hover:from-violet-600 hover:to-fuchsia-600 transition-all
          disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Sparkles className="w-3.5 h-3.5" />
        )}
        {loading ? busyLabel : idleLabel}
      </button>
      {error && (
        <p className="mt-1 text-xs text-red-500 max-w-[16rem] text-right">{error}</p>
      )}
    </div>
  );
}
